<template>
  <div class="comment__idea-audit">
    <div
      class="comment__idea-resource"
      :class="{ 'is-bordered': !isEpic }"
    >
      <div
        v-if="isEpic"
        class="comment__idea-resource-summary"
      >
        <div class="comment__idea-resource-name">
          <BaseSubHeading
            size="medium"
            weight="500"
            color="monochrome-05"
            darkColor="monochrome-04"
            class="comment__idea-resource-key"
          >
            E-{{ resource?.epicSequenceNumber }}
          </BaseSubHeading>
          <BaseSubHeading
            size="medium"
            weight="400"
            color="monochrome-07"
            class="comment__idea-converted-card__title"
          >
            {{ resource?.epicTitle }}
          </BaseSubHeading>
        </div>
        <div class="comment__idea-converted-card__badges">
          <TShortComponent
            :size="resource?.size ?? 'none'"
            :label="resource?.size || '--'"
          />
        </div>
      </div>
      <div
        v-else-if="isItem"
        class="comment__idea-resource-name"
      >
        <BaseSubHeading
          size="medium"
          weight="500"
          color="monochrome-05"
          darkColor="monochrome-04"
          class="comment__idea-resource-key"
        >
          #{{ resource?.itemSequenceNumber }}
        </BaseSubHeading>
        <div class="comment__idea-converted-card__item-title">
          <span
            v-if="resource?.itemLabel"
            class="custom-label comment__idea-converted-card__label"
          >
            {{ resource.itemLabel }}
          </span>
          <BaseSubHeading
            size="medium"
            weight="400"
            color="monochrome-07"
            class="comment__idea-converted-card__title"
          >
            {{ resource?.itemTitle }}
          </BaseSubHeading>
        </div>
      </div>
      <BaseSubHeading
        v-else
        size="medium"
        weight="400"
        color="monochrome-07"
        class="comment__idea-converted-card__title comment__idea-resource-feature"
      >
        {{ resource?.featureTitle }}
      </BaseSubHeading>
      <span
        class="comment__idea-resource-state"
        :class="{ 'is-obsolete': isObsolete }"
      >
        {{ stateLabel }}
      </span>
    </div>
    <span
      v-if="message"
      class="comment__idea-resource-message"
    >
      {{ message }}
    </span>
    <div
      v-if="beforeStatus && afterStatus"
      class="comment__idea-resource-status"
    >
      <span
        class="comment__idea-resource-status-value is-opacity"
        :class="`is_${beforeStatus.toLowerCase()}`"
      >
        {{ getIdeaStatus(beforeStatus).label }}
      </span>
      <icon
        data="@icon/arrow-to.svg"
        :fill="true"
        width="var(--icon-24)"
        height="var(--icon-24)"
        color="var(--monochrome05to04)"
      />
      <span
        class="comment__idea-resource-status-value"
        :class="`is_${afterStatus.toLowerCase()}`"
      >
        {{ getIdeaStatus(afterStatus).label }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"

import TShortComponent from "@/components/TShort/index.vue"

import { Comment } from "@/models"
import { stripHtml } from "@/utils/helpers"
import { getIdeaStatus } from "@/views/Ideas/constants"

/** History entry for an Idea whose converted Epic, Item or Roadmap Deliverable was deleted, archived or marked Obsolete. */
const props = defineProps<{
  comment: Comment
}>()

const change = computed(() => props.comment.ideaAuditRecord?.change)
const resource = computed(() => props.comment.ideaAuditRecord?.after)
const beforeStatus = computed(() => props.comment.ideaAuditRecord?.before?.status)
const afterStatus = computed(() => resource.value?.status)
const isEpic = computed(() => change.value === "EPIC_DELETED" || change.value === "EPIC_ARCHIVED")
const isItem = computed(() => change.value === "ITEM_DELETE" || change.value === "ITEM_OBSOLETE")
const isObsolete = computed(() => change.value === "ITEM_OBSOLETE")
const stateLabel = computed(() => {
  if (change.value === "EPIC_ARCHIVED") return "Archived"
  return isObsolete.value ? "Obsolete" : "Deleted"
})
const message = computed(() => stripHtml(props.comment.structuredDescription?.html ?? ""))
</script>
